import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui';
import { resolveInstallOffer } from '../../utils/installPrompt';
import { readInstallEnvironment, safely } from '../../utils/installEnvironment';
import { wordsOf } from './todoFormat';

type View = 'checking' | 'failed' | 'no-key' | 'iphone' | 'unsupported' | 'denied' | 'off' | 'on';

const WORDS: Partial<Record<View, string>> = {
  'no-key': 'Reminders are not switched on for the app yet.',
  iphone: 'On iPhone, reminders work from the home-screen app: Share, then Add to Home Screen, then open Apex from there.',
  unsupported: "This browser can't show reminders.",
  denied: 'Notifications are blocked for Apex - allow them in Settings, Notifications.',
  on: 'Reminders are on for this device (7:30 AM, 1 PM, 3:30 PM)',
};
/** ⭐ R33: pushService sends nothing until his clean-up is finished, so "on" must not read as "coming at 7:30". */
const WAITING = 'They start after the clean-up, or when you tap Start reminders.';
const NOT_CHECKED = "Couldn't check reminders on this device - try again.";
const BROWSER_SAID_NO = "This browser didn't turn reminders on - try again.";
const NOT_OFF = 'Reminders were not switched off - try again.';

/**
 * ⚠️ `main.tsx` registers /sw.js in PRODUCTION builds only. In dev there is no worker, `serviceWorker.ready` never
 * resolves and the button would hang - so dev says, honestly, that this browser can't.
 */
const canPush = (): boolean =>
  import.meta.env.PROD && safely(() => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window, false);

/**
 * ⭐ An iPhone tab has no PushManager at all, so this is asked BEFORE `canPush` - or he is told his phone can't do
 * what its home-screen app can. The one reader of "is this the installed app?" (`installEnvironment.ts`), not a second.
 */
const iPhoneTab = (): boolean => resolveInstallOffer(readInstallEnvironment(false)) === 'ios-instructions';

/** The server's VAPID key travels as url-safe base64 without padding; `pushManager.subscribe` wants its bytes. */
const keyBytes = (key: string): Uint8Array<ArrayBuffer> => {
  const b64 = (key + '='.repeat((4 - (key.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
};

/** The permission prompt (only ever on his tap), then the browser's subscription - or the answer that stopped it. */
const browserSubscribe = async (key: string): Promise<PushSubscription | NotificationPermission> => {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission;
  const reg = await navigator.serviceWorker.ready;
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) });
};

/**
 * TODO1 - reminders on THIS device (spec 4.2): a push at 7:30 AM, 1 PM and 3:30 PM Pacific. One subscription per device;
 * ⛔ nothing is asked or saved until he taps - the page writes nothing on load.
 * ⚠️ "On" is read from the browser's subscription: there is no read of the server's list, and re-saving on load would
 * be a write on load.
 */
export default function PushOptIn({ sweepFinished }: { sweepFinished: boolean }) {
  const [view, setView] = useState<View>('checking');
  const [key, setKey] = useState('');
  const [sub, setSub] = useState<PushSubscription | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    const failed = (words: string) => {
      if (!live) return;
      setError(words);
      setView('failed');
    };
    (async () => {
      let publicKey: string | null;
      try {
        ({ publicKey } = await api.todos.pushKey());
      } catch (e) {
        return failed(wordsOf(e, NOT_CHECKED)); // the server's words
      }
      if (!live) return;
      if (!publicKey) return setView('no-key');
      setKey(publicKey);
      if (iPhoneTab()) return setView('iphone');
      if (!canPush()) return setView('unsupported');
      if (Notification.permission === 'denied') return setView('denied');
      try {
        // getRegistration, not ready: it answers at once, even before main.tsx has registered the worker.
        const reg = await navigator.serviceWorker.getRegistration();
        const existing = reg ? await reg.pushManager.getSubscription() : null;
        if (!live) return;
        setSub(existing);
        setView(existing ? 'on' : 'off');
      } catch {
        failed(NOT_CHECKED); // the browser's own error is not his to read
      }
    })();
    return () => { live = false; };
  }, []);

  const turnOn = async () => {
    setBusy(true);
    setError(null);
    try {
      let created: PushSubscription | NotificationPermission;
      try {
        created = await browserSubscribe(key);
      } catch {
        // The browser's own error is not his to read (e.g. "Registration failed - push service error").
        setError(BROWSER_SAID_NO);
        return;
      }
      if (created === 'denied') return setView('denied');
      if (typeof created === 'string') return; // dismissed: the button stays for another try
      try {
        await api.todos.subscribe(created.toJSON(), navigator.userAgent);
      } catch (e) {
        // ⛔ Not saved on the server = no reminders. Drop the browser's half too, or the next visit would say "on".
        await created.unsubscribe().catch(() => false);
        setError(wordsOf(e, BROWSER_SAID_NO));
        return;
      }
      setSub(created);
      setView('on');
    } finally {
      setBusy(false);
    }
  };

  const turnOff = async () => {
    if (!sub) return;
    setBusy(true);
    setError(null);
    try {
      // The server first: if it refuses, nothing has changed, "on" is still true and he can try again.
      await api.todos.unsubscribe(sub.endpoint);
    } catch (e) {
      setError(wordsOf(e, NOT_OFF));
      setBusy(false);
      return;
    }
    // The server sends this device nothing more now; a browser that will not let go keeps only a dead subscription.
    await sub.unsubscribe().catch(() => false);
    setSub(null);
    setView('off');
    setBusy(false);
  };

  if (view === 'checking') return null;
  const words = WORDS[view];
  return (
    <section aria-label="Reminders on this device" className="mb-4 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {words && (
          <div>
            <p>{words}</p>
            {view === 'on' && !sweepFinished && <p className="mt-1 text-gray-500">{WAITING}</p>}
          </div>
        )}
        {view === 'off' && (
          <Button size="sm" onClick={() => void turnOn()} isLoading={busy}>Turn on reminders on this phone</Button>
        )}
        {view === 'on' && (
          <Button size="sm" variant="outline" onClick={() => void turnOff()} isLoading={busy}>Turn off on this device</Button>
        )}
      </div>
      {error && <p role="alert" className="mt-2 text-red-700">{error}</p>}
    </section>
  );
}
